import styled from "@emotion/styled";
import { ErrorMessage } from "@hookform/error-message";
import { yupResolver } from "@hookform/resolvers/yup";
import { KeyboardArrowDown } from "@mui/icons-material";
import {
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { Trash } from "@phosphor-icons/react";
import { Button } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { useChurchStore } from "features/churches";
import {
  useBudgetYears,
  useChartOfAccounts,
  useCreateDefaultChartOfAccounts,
  useCreateRequisition,
  useTeamBudgets,
  useUpdateRequisition
} from "features/finances";
import { FILTER_OPERATIONS } from "features/members";
import { useTeams } from "features/teams";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useAuthUser } from "react-auth-kit";
import { useDropzone } from "react-dropzone";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { EthiopianDateField } from "../EthiopianDateField";
import { ValidationError } from "../ValidationError";

const FormContainer = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 900px;
  align-self: flex-start;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
  width: 100%;
  box-sizing: border-box;
  ${props =>
    props.error &&
    `
    border-color: #d32f2f;
    &:focus {
      border-color: #d32f2f;
    }
  `}
`;

const Select = styled.select`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
  font-family: inherit;
  background-color: transparent;

  ${props =>
    props.error &&
    `
    border-color: #d32f2f;
    &:focus {
      border-color: #d32f2f;
    }
  `}
`;

const AmountContainer = styled.div`
  position: relative;

  &::after {
    content: "ETB";
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #5b5b5b;
    font-size: 12px;
  }
`;

const TableTitle = styled(Typography)`
  font-weight: bold;
  margin-bottom: 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  background-color: #fafafa;
  padding: 16px;
`;

const StyledButton = styled(Button)`
  border-radius: 100px;
  font-weight: 500;
  background-color: rgba(17, 24, 39, 1);
  border-color: rgba(17, 24, 39, 1);

  &:hover {
    background-color: rgba(17, 24, 39, 1);
    border-color: rgba(17, 24, 39, 1);
  }

  &:disabled {
    background-color: rgba(17, 24, 39, 1);
    border-color: rgba(17, 24, 39, 1);
  }
`;

const CancelButton = styled(StyledButton)`
  background-color: white;
  color: rgb(24, 24, 27);
  border-color: rgba(17, 24, 39, 1);

  &:hover {
    background-color: white;
    color: rgb(24, 24, 27);
    border-color: rgba(17, 24, 39, 1);
  }
`;

const StyledTableHead = styled(TableHead)`
  background-color: #fafafa;

  th {
    font-weight: 600;
  }
`;

const TableHeaderCell = styled(TableCell)`
  font-size: 12px;
  color: rgba(142, 142, 142, 1);
`;

const ErrorText = styled.span`
  color: #d32f2f;
  font-size: 12px;
  margin-top: 4px;
`;

const Filename = styled.span`
  font-size: 10px;
`;

const DropzoneContainer = styled.div`
  border: 1px dashed #b3b3b3;
  border-radius: 4px;
  padding: 20px;
  text-align: center;
  cursor: pointer;

  &:hover {
    border-color: #3dadb7;
  }
`;

const DropzoneText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #5b5b5b;
`;

const DropzoneSubText = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  color: #b3b3b3;
`;

const RequestButton = styled(Button)`
  border-radius: 0;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  font-weight: 500;
  background-color: ${({ theme }) => theme.palette.primary.main};
  border-color: ${({ theme }) => theme.palette.primary.main};
  border-right-color: #fff;
`;

const requisitionSchema = yup.object().shape({
  purpose: yup.string().required("Purpose is required"),
  requestType: yup.string().required("Request type is required"),
  totalAmount: yup.number().required("Total amount is required"),
  requestedBy: yup.string().required("Requested by is required"),
  department: yup.string().required("Department is required"),
  requestedDate: yup
    .date()
    .required("Requested date is required")
    .nullable(true),
  dueDate: yup.date().required("Due date is required").nullable(true),
  is_requisition_against_budget: yup.string().notRequired(),
  budget_year_id: yup.string().notRequired(),
  budget_item_id: yup.string().notRequired(),
  items: yup
    .array()
    .of(
      yup.object().shape({
        expense_category_id: yup
          .string()
          .required("Expense category is required")
          .typeError("Expense category is invalid"),
        description: yup.string().notRequired(),
        item_amount: yup
          .number()
          .typeError("Item amount must be a number")
          .required("Item amount is required")
      })
    )
    .notRequired()
});

export const RequisitionRequestForm = ({
  isView = false,
  isEditMode = false,
  data
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [
    isAdvanceChartOfAccountsGenerated,
    setIsAdvanceChartOfAccountsGenerated
  ] = useState(true);

  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const auth = useAuthUser();

  const userId = auth()?.id;

  const onDrop = useCallback(acceptedFiles => {
    setSelectedFile(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "application/pdf": [".pdf"]
    },
    maxSize: 2 * 1024 * 1024, // 2 MB
    multiple: false
  });

  const teamsQuery = useTeams({
    churchId: selectedChurch?.id
  });

  const chartOfAccountsQuery = useChartOfAccounts({
    churchId: selectedChurch?.id,
    filters: [
      { column: "account_type", operator: "in", value: ["EXPENSE", "ASSET"] }
    ]
  });

  const createRequisitionMutation = useCreateRequisition();
  const updateRequisitionMutation = useUpdateRequisition();
  const createDefaultChartOfAccountsMutation =
    useCreateDefaultChartOfAccounts();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
    setValue
  } = useForm({
    resolver: yupResolver(requisitionSchema),
    defaultValues: {
      purpose: data?.reason || "",
      requestType: data?.request_type || "ADVANCE",
      totalAmount: data?.amount || 0,
      requestedBy: userId || "",
      department: data?.team?.id || "",
      requestedDate:
        moment(data?.requested_date).format("YYYY-MM-DD") ||
        moment().format("YYYY-MM-DD"),
      dueDate:
        moment(data?.due_date).format("YYYY-MM-DD") ||
        moment().format("YYYY-MM-DD"),
      is_requisition_against_budget: data?.is_requisition_against_budget
        ? String(data?.is_requisition_against_budget)
        : "0",
      budget_year_id: data?.budget_year_id || "",
      budget_item_id: data?.budget_item_id || "",
      items: data?.line_items?.map(item => ({
        expense_category_id: item.expense_category_id,
        description: item.description,
        item_amount: item.item_amount
      })) || [
        {
          expense_category_id: "",
          description: "",
          item_amount: 0
        }
      ]
    }
  });

  const requestedDate = watch("requestedDate");
  const dueDate = watch("dueDate");

  useEffect(() => {
    if (userId) {
      setValue("requestedBy", userId);
    }
  }, [userId]);

  useEffect(() => {
    if (teamsQuery.data) {
      setValue("department", data?.team?.id);
    }
  }, [teamsQuery.data]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items"
  });

  const isSettlement = watch("requestType") === "SETTLEMENT";
  const isAdvance = watch("requestType") === "ADVANCE";
  const items = watch("items");

  const hasFilledItems = items?.every(
    item => item.expense_category_id && item.description && item.item_amount
  );

  // Add effect to handle advance request type
  useEffect(() => {
    if (isAdvance && chartOfAccountsQuery.data?.result) {
      // Find the advance account
      const advanceAccount = chartOfAccountsQuery.data.result.find(
        account => account.account_name.toLowerCase() === "advance"
      );

      if (advanceAccount) {
        // Set expense category to advance for all items
        items?.forEach((_, index) => {
          setValue(`items.${index}.expense_category_id`, advanceAccount.id);
        });
      } else {
        setIsAdvanceChartOfAccountsGenerated(false);
      }
    }
  }, [isAdvance, chartOfAccountsQuery.data, items, setValue]);

  const budgetYearsQuery = useBudgetYears({
    churchId: selectedChurch?.id,
    config: {
      enabled:
        watch("is_requisition_against_budget") === "1" && !!watch("department")
    }
  });

  // Add effect to set default budget year
  useEffect(() => {
    if (budgetYearsQuery.data?.result) {
      const currentYear = moment().year();
      const currentBudgetYear = budgetYearsQuery.data.result.find(
        budgetYear => moment(budgetYear.start_date).year() === currentYear
      );
      if (currentBudgetYear) {
        setValue("budget_year_id", currentBudgetYear.id);
      }
    }
  }, [budgetYearsQuery.data, setValue]);

  const budgetItemsQuery = useTeamBudgets({
    churchId: selectedChurch?.id,
    teamId: watch("department"),
    queries: {
      filter: {
        budget_year_id: [
          {
            operator: FILTER_OPERATIONS.EQUAL,
            value: watch("budget_year_id")
          }
        ]
      }
    },
    config: {
      enabled:
        watch("is_requisition_against_budget") === "1" &&
        !!watch("department") &&
        !!watch("budget_year_id")
    }
  });

  const onSubmit = async (values, status = "PENDING") => {
    const requisitionData = {
      amount: values.totalAmount,
      reason: values.purpose,
      request_type: values.requestType,
      team_id: values.department,
      user_id: values.requestedBy,
      due_date: moment(values.dueDate).format("YYYY-MM-DD"),
      requested_date: moment(values.requestedDate).format("YYYY-MM-DD"),
      line_items: values.items,
      requisition_status: status,
      is_requisition_against_budget: values.is_requisition_against_budget,
      budget_item_id: values.budget_item_id,
      budget_year_id: values.budget_year_id
    };

    const formData = new FormData();

    for (const [key, value] of Object.entries(requisitionData)) {
      if (key === "line_items") {
        value.forEach((item, index) => {
          formData.append(
            `line_items[${index}][expense_category_id]`,
            item.expense_category_id
          );
          formData.append(
            `line_items[${index}][description]`,
            item.description
          );
          formData.append(
            `line_items[${index}][item_amount]`,
            item.item_amount
          );
        });
      } else {
        formData.append(key, value);
      }
    }

    if (isSettlement && selectedFile) {
      formData.append("item_attachment", selectedFile);
    }

    try {
      // handles draft requisition submits
      if (isEditMode || (isView && data)) {
        updateRequisitionMutation.mutate({
          churchId: selectedChurch?.id,
          requisitionId: data?.id,
          requisitionData: formData
        });
      } else {
        createRequisitionMutation.mutate({
          churchId: selectedChurch?.id,
          requisitionData: formData
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleGenerateAdvanceChartOfAccount = () => {
    createDefaultChartOfAccountsMutation.mutate(
      {
        churchId: selectedChurch?.id,
        accountData: {
          account_names: ["Advance"]
        }
      },
      {
        onSuccess: () => {
          setIsAdvanceChartOfAccountsGenerated(true);
        },
        onError: () => {
          setIsAdvanceChartOfAccountsGenerated(false);
        }
      }
    );
  };

  return (
    <Stack spacing={2}>
      <Typography fontWeight="bold" variant="body2">
        General Request Information
      </Typography>

      <FormContainer
        onSubmit={() => {
          handleSubmit(onSubmit)("PENDING");
        }}
      >
        <Field>
          <Label htmlFor="purpose">Purpose *</Label>
          <Input
            id="purpose"
            type="text"
            placeholder="Write purpose here"
            {...register("purpose")}
            disabled={isView}
          />
          <ErrorMessage
            errors={errors}
            name="purpose"
            render={({ message }) => <ValidationError message={message} />}
          />
        </Field>

        <Field>
          <Label htmlFor="requestType">Request type *</Label>
          <Select
            id="requestType"
            {...register("requestType")}
            disabled={isView}
          >
            <option value="ADVANCE">Advance</option>
            <option value="REIMBURSEMENT">Reimbursement</option>
            <option value="SETTLEMENT">Settlement</option>
          </Select>
          <ErrorMessage
            errors={errors}
            name="requestType"
            render={({ message }) => <ValidationError message={message} />}
          />
        </Field>

        <Field>
          <Label htmlFor="totalAmount">Total amount *</Label>
          <AmountContainer>
            <Controller
              name="totalAmount"
              control={control}
              render={({ field }) => (
                <NumericFormat
                  customInput={Input}
                  id="totalAmount"
                  thousandSeparator
                  allowNegative={false}
                  onValueChange={values => {
                    field.onChange(values.floatValue);
                  }}
                  value={field.value}
                  disabled={isView}
                />
              )}
            />
          </AmountContainer>
          <ErrorMessage
            errors={errors}
            name="totalAmount"
            render={({ message }) => <ValidationError message={message} />}
          />
        </Field>

        <Field>
          <Label htmlFor="requestedBy">Requested by *</Label>
          <Select id="requestedBy" {...register("requestedBy")} disabled>
            <option value={userId}>
              {auth()?.first_name} {auth()?.last_name}
            </option>
          </Select>
          <ErrorMessage
            errors={errors}
            name="requestedBy"
            render={({ message }) => <ValidationError message={message} />}
          />
        </Field>

        <Field>
          <Label htmlFor="department">Department *</Label>
          <Select id="department" {...register("department")} disabled={isView}>
            <option value="" disabled>
              Select Department
            </option>
            {teamsQuery.data?.result?.map(team => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </Select>
          <ErrorMessage
            errors={errors}
            name="department"
            render={({ message }) => <ValidationError message={message} />}
          />
        </Field>

        <Field>
          <Label>Requested date *</Label>
          <EthiopianDateField
            size="small"
            value={requestedDate}
            onChange={e => setValue("requestedDate", e.target.value)}
            disabled={isView}
          />
          <ErrorMessage
            errors={errors}
            name="requestedDate"
            render={({ message }) => <ValidationError message={message} />}
          />
        </Field>

        <Field>
          <Label>Due date *</Label>
          <EthiopianDateField
            size="small"
            value={dueDate}
            onChange={e => setValue("dueDate", e.target.value)}
            disabled={isView}
          />
          <ErrorMessage
            errors={errors}
            name="dueDate"
            render={({ message }) => <ValidationError message={message} />}
          />
        </Field>

        {isSettlement && (
          <Field>
            <Label>Attachment</Label>
            <DropzoneContainer {...getRootProps()}>
              <input {...getInputProps()} />
              <DropzoneText>Click to upload or drag and drop</DropzoneText>
              <DropzoneSubText>PNG, JPG or PDF (max. 2 MB)</DropzoneSubText>
            </DropzoneContainer>
            {selectedFile && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mt: 1 }}
              >
                <Filename>{selectedFile.name}</Filename>
                <IconButton size="small" onClick={() => setSelectedFile(null)}>
                  <Trash fontSize="small" />
                </IconButton>
              </Stack>
            )}
            {fileRejections.length > 0 && (
              <Alert severity="error">
                {fileRejections
                  .map(({ errors }) =>
                    errors.map(error => {
                      if (error.code === "file-too-large") {
                        return "File size is too large. Maximum size is 2 MB.";
                      }
                      if (error.code === "file-invalid-type") {
                        return "Invalid file type. Please upload PNG, JPG or PDF files only.";
                      }
                      return error.message;
                    })
                  )
                  .join(", ")}
              </Alert>
            )}
            <ErrorMessage
              errors={errors}
              name="attachment"
              render={({ message }) => <ValidationError message={message} />}
            />
          </Field>
        )}

        <Field>
          <Label>Is Requisition against budget?</Label>
          <Select
            id="is_requisition_against_budget"
            {...register("is_requisition_against_budget")}
            disabled={isView}
          >
            <option value="" disabled>
              Select is requisition against budget
            </option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </Select>
          <ErrorMessage
            errors={errors}
            name="is_requisition_against_budget"
            render={({ message }) => <ValidationError message={message} />}
          />
        </Field>

        {watch("is_requisition_against_budget") === "1" && (
          <Field>
            <Label>Budget Year</Label>
            <Select
              id="budget_year_id"
              {...register("budget_year_id")}
              disabled={isView}
            >
              <option value="" disabled>
                Select Budget Year
              </option>
              {budgetYearsQuery.data?.result
                ?.filter(budgetYear => {
                  const currentYear = moment().year();
                  const budgetYearStart = moment(budgetYear.start_date).year();
                  return budgetYearStart === currentYear;
                })
                .map(budgetYear => (
                  <option key={budgetYear.id} value={budgetYear.id}>
                    {budgetYear.title}
                  </option>
                ))}
            </Select>
            <ErrorMessage
              errors={errors}
              name="budget_year_id"
              render={({ message }) => <ValidationError message={message} />}
            />
          </Field>
        )}

        {watch("is_requisition_against_budget") === "1" && (
          <Field>
            <Label>Budget Item</Label>
            <Select
              id="budget_item_id"
              {...register("budget_item_id")}
              disabled={isView}
            >
              <option value="" disabled>
                Select Budget Item
              </option>
              {budgetItemsQuery.data?.result?.data?.map(budgetItem => (
                <option key={budgetItem.id} value={budgetItem.id}>
                  {budgetItem.item}
                </option>
              ))}
            </Select>
            <ErrorMessage
              errors={errors}
              name="budget_item_id"
              render={({ message }) => <ValidationError message={message} />}
            />
          </Field>
        )}
      </FormContainer>

      {isAdvance && !isAdvanceChartOfAccountsGenerated && (
        <Alert severity="error">
          <Typography sx={{ fontSize: "14px" }}>
            Before you can request an advance, you need to set up the advance
            chart of account.{" "}
            <Typography
              component="span"
              onClick={handleGenerateAdvanceChartOfAccount}
              sx={{
                cursor: "pointer",
                color: "primary.main",
                fontSize: "14px",
                ":hover": {
                  textDecoration: "underline"
                }
              }}
              disabled={createDefaultChartOfAccountsMutation.isLoading}
            >
              Generate advance chart of account
            </Typography>
          </Typography>
        </Alert>
      )}

      <Stack spacing={2}>
        <TableTitle variant="body2">Add Items</TableTitle>
        <TableContainer>
          <Table>
            <StyledTableHead>
              <TableRow>
                <TableHeaderCell>Ref No.</TableHeaderCell>
                <TableHeaderCell>Expense Category</TableHeaderCell>
                <TableHeaderCell>Description</TableHeaderCell>
                <TableHeaderCell>Amount</TableHeaderCell>
                {!isView && <TableHeaderCell>Actions</TableHeaderCell>}
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {fields.map((field, index) => (
                <TableRow key={field.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Select
                      {...register(`items.${index}.expense_category_id`)}
                      error={!!errors.items?.[index]?.expense_category_id}
                      disabled={isView || isAdvance}
                    >
                      <option value="" disabled>
                        Select expense category
                      </option>
                      {chartOfAccountsQuery.data?.result?.map(
                        chartOfAccount => (
                          <option
                            key={chartOfAccount.id}
                            value={chartOfAccount.id}
                          >
                            {chartOfAccount.account_name}
                          </option>
                        )
                      )}
                    </Select>
                    {errors.items?.[index]?.expense_category_id && (
                      <ErrorText>
                        {errors.items[index].expense_category_id.message}
                      </ErrorText>
                    )}
                  </TableCell>
                  <TableCell>
                    {isView ? (
                      <span>{data?.line_items?.[index]?.description}</span>
                    ) : (
                      <Input
                        type="text"
                        {...register(`items.${index}.description`)}
                        error={!!errors.items?.[index]?.description}
                        disabled={isView}
                      />
                    )}
                    {errors.items?.[index]?.description && (
                      <ErrorText>
                        {errors.items[index].description.message}
                      </ErrorText>
                    )}
                  </TableCell>
                  <TableCell>
                    <AmountContainer>
                      {isView ? (
                        <span>
                          {Number(
                            data?.line_items?.[index]?.item_amount
                          ).toLocaleString()}
                        </span>
                      ) : (
                        <Controller
                          name={`items.${index}.item_amount`}
                          control={control}
                          render={({ field }) => (
                            <NumericFormat
                              customInput={Input}
                              id="item_amount"
                              thousandSeparator
                              allowNegative={false}
                              onValueChange={values => {
                                field.onChange(values.floatValue);
                              }}
                              value={field.value}
                              disabled={isView}
                            />
                          )}
                        />
                      )}
                    </AmountContainer>
                    {errors.items?.[index]?.item_amount && (
                      <ErrorText>
                        {errors.items[index].item_amount.message}
                      </ErrorText>
                    )}
                  </TableCell>
                  {!isView && (
                    <TableCell>
                      <IconButton
                        type="button"
                        onClick={() => remove(index)}
                        flavor="outlined"
                        size="small"
                      >
                        <Trash />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!isView && (
            <ButtonGroup>
              <StyledButton
                type="button"
                style={{ width: "120px" }}
                onClick={() =>
                  append({
                    expense_category_id: "",
                    description: "",
                    amount: 0
                  })
                }
                disabled={items?.length > 0 && !hasFilledItems}
              >
                Add items
              </StyledButton>
              {items?.length > 1 && (
                <CancelButton
                  type="button"
                  style={{ width: "120px" }}
                  onClick={() => remove()}
                >
                  Clear items
                </CancelButton>
              )}
            </ButtonGroup>
          )}
        </TableContainer>
      </Stack>

      {!isView && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          spacing={2}
        >
          {/* Show Submit Request button for new requests or non-drafted status */}
          {(!data || data.requisition_status !== "DRAFTED") && (
            <Stack direction="row">
              <RequestButton
                type="submit"
                onClick={() => {
                  handleSubmit(onSubmit)("PENDING");
                }}
                disabled={
                  createRequisitionMutation.isLoading ||
                  updateRequisitionMutation.isLoading
                }
              >
                {isEditMode ? "Save" : "Submit Request"}
              </RequestButton>
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  backgroundColor: "primary.main",
                  color: "white",
                  padding: "4px",
                  cursor: "pointer",
                  borderTopRightRadius: "4px",
                  borderBottomRightRadius: "4px"
                }}
                onClick={handleClick}
                disabled={
                  createRequisitionMutation.isLoading ||
                  updateRequisitionMutation.isLoading
                }
              >
                <KeyboardArrowDown />
              </Stack>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left"
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right"
                }}
              >
                <MenuItem
                  onClick={() => {
                    handleSubmit(onSubmit)("DRAFTED");
                  }}
                >
                  <Typography variant="body2">Save As Draft</Typography>
                </MenuItem>
              </Menu>
            </Stack>
          )}
          {/* Show Save button for drafted status in edit mode */}
          {data && data.requisition_status === "DRAFTED" && (
            <Button
              type="submit"
              onClick={() => {
                handleSubmit(onSubmit)("DRAFTED");
              }}
              disabled={
                createRequisitionMutation.isLoading ||
                updateRequisitionMutation.isLoading
              }
            >
              Save
            </Button>
          )}
          <Button
            flavor="outlined"
            type="button"
            onClick={() => {
              reset();
              navigate(-1);
            }}
            disabled={
              createRequisitionMutation.isLoading ||
              updateRequisitionMutation.isLoading
            }
          >
            Cancel
          </Button>
        </Stack>
      )}

      {/* Show Submit Request button for drafted status in view mode */}
      {isView && data && data.requisition_status === "DRAFTED" && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          spacing={2}
        >
          <Button
            type="submit"
            onClick={() => {
              handleSubmit(onSubmit)("PENDING");
            }}
            disabled={
              createRequisitionMutation.isLoading ||
              updateRequisitionMutation.isLoading
            }
          >
            Submit Request
          </Button>
          <Button
            flavor="outlined"
            type="button"
            onClick={() => {
              reset();
              navigate(-1);
            }}
            disabled={
              createRequisitionMutation.isLoading ||
              updateRequisitionMutation.isLoading
            }
          >
            Cancel
          </Button>
        </Stack>
      )}

      <MutationError
        error={
          createRequisitionMutation.error || updateRequisitionMutation.error
        }
        sx={{ mt: 2 }}
      />
    </Stack>
  );
};
